/**
 * One voice at a time, page-wide (kid-practice §5.3).
 *
 * A debrief can show a dozen "Hear it" buttons, and a kid's card plays the
 * tutor's example beside the child's own recording: two taps would otherwise
 * layer two voices (cited-errors §5). Page-wide rather than per component,
 * which is what `HearIt`'s module-level `nowPlaying` was before this.
 */
export type Voice = { pause(): void };

let holder: Voice | null = null;
let holds = 0;

/**
 * `v` is about to play. Refused, and `v` paused rather than left running,
 * while a recording holds the page (storybooks PRD amendment M4): otherwise
 * a synthesis landing mid-recording would play into the microphone, since
 * nothing captures it until the caller's own next `claim`. A caller that
 * goes on to call `.play()` regardless defeats this, so every `claim` site
 * must check the return.
 */
export function claim(v: Voice): boolean {
  if (holds > 0) {
    v.pause();
    return false;
  }
  if (holder !== v) holder?.pause();
  holder = v;
  return true;
}

/**
 * Whether `v` is still the one playing. A clip another voice took over is
 * paused, which rejects its `play()` with an `AbortError`: superseded, not
 * failed.
 */
export function isCurrent(v: Voice): boolean {
  return holder === v;
}

/** Nothing plays: a recording is starting, and must not capture the model. */
export function silence(): void {
  holder?.pause();
  holder = null;
}

/**
 * A recording is about to start: every `claim` refuses until the returned
 * release runs, and whatever is playing now is silenced immediately, which
 * subsumes a plain `silence()` at that moment. Counted, not a flag, because
 * two holds can overlap (one recorder's teardown racing another's start);
 * the page unblocks only once every hold has released, and a release is
 * harmless to call more than once (storybooks PRD amendment M4).
 */
export function hold(): () => void {
  silence();
  holds++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds--;
  };
}
